import { ArrowUpRight, ChartNoAxesColumn, CheckCircle2, Clock, Database, Link2, RefreshCw, Settings2, Workflow } from "lucide-react";
import { Link } from "react-router-dom";

const SECTION_ICONS = {
  mrr: ChartNoAxesColumn,
  arr: Workflow,
  connection: Link2,
  settings: Settings2,
};

const SECTION_ACCENT = {
  mrr: "from-[#50e3c2]/10 to-transparent border-[#57c1a9]/20 hover:border-[#57c1a9]/40",
  arr: "from-violet-500/10 to-transparent border-violet-500/20 hover:border-violet-500/40",
  connection: "from-sky-500/10 to-transparent border-sky-500/20 hover:border-sky-500/40",
  settings: "from-slate-400/10 to-transparent border-slate-400/20 hover:border-slate-400/30",
};

const SECTION_ICON_BG = {
  mrr: "bg-[#50e3c2]/15 text-[#119996]",
  arr: "bg-violet-500/15 text-violet-400",
  connection: "bg-sky-500/15 text-sky-400",
  settings: "bg-slate-500/15 text-slate-400",
};

function SyncStatusChip({ connection }) {
  if (!connection) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
        No portal connected
      </span>
    );
  }
  if (connection.sync_status === "running") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Syncing…
      </span>
    );
  }
  if (connection.sync_status === "success") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
        <CheckCircle2 className="h-3 w-3" />
        {connection.portal_name || `Portal ${connection.portal_id}`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600">
      <Clock className="h-3 w-3" />
      Awaiting sync
    </span>
  );
}

function StatCard({ label, value, caption, icon: Icon, testId }) {
  return (
    <div
      className="flex flex-col justify-between rounded-xl border border-[#dddddd] bg-white p-5 shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <p
          className="text-[11px] font-semibold uppercase tracking-widest text-slate-400"
          data-testid={`${testId}-label`}
        >
          {label}
        </p>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#323c44]/5">
          <Icon className="h-4 w-4 text-[#323c44]/50" />
        </div>
      </div>
      <div>
        <p
          className="mt-4 font-sans text-2xl font-bold text-[#323c44]"
          data-testid={`${testId}-value`}
        >
          {value}
        </p>
        <p
          className="mt-1 text-xs text-slate-400"
          data-testid={`${testId}-caption`}
        >
          {caption}
        </p>
      </div>
    </div>
  );
}

function SectionCard({ card }) {
  const Icon = SECTION_ICONS[card.icon];
  const accent = SECTION_ACCENT[card.icon] || SECTION_ACCENT.settings;
  const iconBg = SECTION_ICON_BG[card.icon] || SECTION_ICON_BG.settings;

  return (
    <Link
      to={card.href}
      className={`group flex flex-col rounded-xl border bg-gradient-to-br ${accent} bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md`}
      data-testid={card.testId}
    >
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors duration-150 group-hover:text-slate-500" />
      </div>
      <div className="mt-4 flex-1">
        <h3
          className="font-sans text-base font-bold text-[#323c44]"
          data-testid={`${card.testId}-title`}
        >
          {card.title}
        </h3>
        <p
          className="mt-1.5 text-sm leading-relaxed text-slate-500"
          data-testid={`${card.testId}-description`}
        >
          {card.description}
        </p>
      </div>
    </Link>
  );
}

export default function WorkspaceHome({ summaryItems, cards, activeConnection }) {
  const statIcons = [Database, Clock, ChartNoAxesColumn];

  return (
    <div className="min-h-full px-6 py-8 lg:px-10 lg:py-10" data-testid="workspace-home-page">
      {/* ── Page header ── */}
      <div className="mb-8" data-testid="workspace-home-hero">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Dashboard
            </p>
            <h1
              className="mt-1 font-sans text-3xl font-extrabold tracking-tight text-[#323c44] sm:text-4xl"
              data-testid="workspace-home-title"
            >
              Revenue workspace
            </h1>
          </div>
          <SyncStatusChip connection={activeConnection} />
        </div>
        <p
          className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500"
          data-testid="workspace-home-description"
        >
          Your HubSpot revenue data, organised. Report on MRR, automate ARR writeback, and manage portal connections from one place.
        </p>
      </div>

      {/* ── Stats strip ── */}
      <div
        className="mb-8 grid gap-4 sm:grid-cols-3"
        data-testid="workspace-summary-strip"
      >
        {summaryItems.map((item, i) => (
          <StatCard
            key={item.label}
            label={item.label}
            value={item.value}
            caption={item.caption}
            icon={statIcons[i] || Database}
            testId={item.testId}
          />
        ))}
      </div>

      {/* ── Section cards ── */}
      <div
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="workspace-card-grid"
      >
        {cards.map((card) => (
          <SectionCard key={card.href} card={card} />
        ))}
      </div>
    </div>
  );
}
